/* eslint-disable react-hooks/exhaustive-deps */
import React, { SyntheticEvent } from "react";
import {
    Autocomplete,
    CircularProgress,
    Stack,
} from "@mui/material";
import { debounce } from "lodash";
import { OutlinedTextField } from "../../../../Components/formComponents";
import { axios_api } from "../../../../api/axios-api";
import { IProductCustomization, defaultProductCustomization } from "../../../../redux/inventory";
import { GET_PRODUCT_CUSTOMIZATION_BASE_URL } from "../../../../api/axios-api.endpoints";

export interface ICustomizationAutoSearchProps {
    label?: string;
    name: string;
    value: {
        customization_uuid: string | null,
        customization_name: string | null,
    };
    onSelect: (data: IProductCustomization) => void;
    disabled?: boolean;
    error?: string;
}

const INITIAL_STATE: IProductCustomization = defaultProductCustomization;

export const CustomizationAutoSearch: React.FC<ICustomizationAutoSearchProps> = ({
    label, name, value, disabled, error, onSelect
}) => {

    const [options, setOptions] = React.useState<readonly IProductCustomization[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [search, setSearchText] = React.useState<string>("");
    const [openRightPanel, setOpenRightPanel] = React.useState<boolean>(false);

    const fetchSuggestion = async (enteredValue: string) => {
        setLoading(true);
        try {
            let searchQuery = "";
            if (enteredValue.length > 0) {
                searchQuery += `columns=customization_name&value=${enteredValue}&`;
            }
            const res = await axios_api.get(
                `${GET_PRODUCT_CUSTOMIZATION_BASE_URL}?${searchQuery}`,
            );
            const finalData: IProductCustomization[] = res.data.data;
            if (value?.customization_uuid && value?.customization_uuid?.length > 0 && search.length === 0) {
                const option: IProductCustomization = {
                    ...INITIAL_STATE,
                    customization_uuid: value.customization_uuid,
                    customization_name: value.customization_name || "",
                };
                finalData.push(option);
            }
            setOptions(finalData);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };
    const debounceFn = React.useCallback(debounce(fetchSuggestion, 800), []);

    const getOptionLabel = (option: IProductCustomization | string) => {
        if (typeof option === "string") return option || "";
        return option.customization_name || ""
    };

    const getValue = () => {
        return (
            options.find((option) => option.customization_uuid === value.customization_uuid) ||
            null
        );
    };

    const handleToggle = () => {
        setOpenRightPanel(!openRightPanel);
    };

    const handleComplete = (customer: IProductCustomization) => {
        setOptions([customer, ...options]);
        onSelect(customer);
        handleToggle();
    };

    const handleDropDownOnFocus = () => {
        debounceFn("");
    };

    React.useEffect(() => {
        if (search && search.length > 0 && search !== value.customization_name) {
            debounceFn(search);
        }
    }, [search]);

    React.useEffect(() => {
        if (value.customization_uuid && value.customization_uuid.length > 0) {
            const option: IProductCustomization = {
                ...INITIAL_STATE,
                customization_uuid: value.customization_uuid,
                customization_name: value.customization_name || "",
            };
            setOptions([option])
        } else {
            setOptions([]);
        }
    }, [value]);

    return (
        <>
            <Stack direction={"row"} justifyContent={"space-between"} spacing={1}>
                <Autocomplete
                    disablePortal
                    fullWidth
                    freeSolo
                    disabled={disabled}
                    sx={{
                        minWidth: 180,
                        ".MuiOutlinedInput-root": {
                            paddingTop: "2px",
                            paddingBottom: "2px",
                            fontSize: "0.8rem",
                            color: "rgb(38, 38, 38)",
                            width: "100%",
                            backgroundColor: disabled ? "#f8f9fb" : "inherit",
                        },
                    }}
                    getOptionLabel={getOptionLabel}
                    isOptionEqualToValue={(option, value) =>
                        typeof option !== "string" && typeof value !== "string"
                            ? option.customization_name === value.customization_name
                            : option === value
                    }
                    filterOptions={(x) => x}
                    options={options}
                    autoComplete
                    includeInputInList
                    value={getValue()}
                    noOptionsText="No Record Found"
                    onFocus={handleDropDownOnFocus}
                    onChange={(
                        event: SyntheticEvent<Element, Event>,
                        newValue: string | IProductCustomization | null,
                    ) => {
                        if (newValue && typeof newValue !== "string") {
                            onSelect(newValue);
                        }
                    }}
                    onInputChange={(event, newInputValue) => {
                        if ((event && event.type === "change") || !newInputValue) {
                            setSearchText(newInputValue);
                        }
                    }}
                    renderInput={(params) => (
                        <OutlinedTextField
                            name={name}
                            {...params}
                            fullWidth
                            label={label}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {loading && <CircularProgress color="inherit" size={20} />}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }}
                            disabled={disabled}
                            error={error}
                        />
                    )}
                />

            </Stack>
        </>
    );
};
